import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, ArrowLeft, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LOGO_URL } from "@/constants/campaign";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "कुछ गड़बड़ हो गई। पुनः प्रयास करें।";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const authedGet = async (url) => {
  try {
    return await axios.get(url, { withCredentials: true });
  } catch (e) {
    if (e?.response?.status === 401) {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      return await axios.get(url, { withCredentials: true });
    }
    throw e;
  }
};

export default function Admin() {
  const [auth, setAuth] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [supporters, setSupporters] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchSupporters = useCallback(async () => {
    try {
      const res = await authedGet(`${API}/admin/supporters`);
      setSupporters(res.data.supporters);
      setTotal(res.data.total);
    } catch (e) {
      if (e?.response?.status === 401) setAuth(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setAuth(res.data))
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth) fetchSupporters();
  }, [auth, fetchSupporters]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setAuth(data);
      toast.success("लॉगिन सफल");
    } catch (err) {
      setLoginError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setAuth(false);
    setSupporters([]);
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/supporters/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "bjpkiarchana_supporters.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV डाउनलोड हो रही है");
    } catch {
      toast.error("डाउनलोड विफल। पुनः लॉगिन करें।");
    }
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#FFF5ED]">
      <div className="flex h-1.5">
        <div className="flex-1 bg-[#FF9933]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#138808]" />
      </div>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-logo" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="भाजपा की अर्चना" className="h-11 w-11 rounded-full object-cover border border-slate-200" />
            <span className="font-heading text-xl md:text-2xl font-semibold text-slate-900">
              भाजपा की <span className="text-[#FF9933]">अर्चना</span>
              <span className="ml-2 text-xs font-body font-semibold text-slate-400 uppercase tracking-wide">Admin</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#FF9933] transition-colors duration-300">
              <ArrowLeft size={16} /> होम
            </Link>
            {auth && (
              <button
                data-testid="admin-logout-button"
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-red-600 transition-colors duration-300"
              >
                <LogOut size={16} /> लॉगआउट
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        {auth === null && <p className="text-slate-500">जाँच हो रही है...</p>}

        {auth === false && (
          <form
            onSubmit={handleLogin}
            data-testid="admin-login-form"
            className="bg-white p-6 md:p-8 rounded-lg shadow-xl border border-slate-100 max-w-md mx-auto"
          >
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">एडमिन लॉगिन</h1>
            <p className="text-sm text-slate-500 mt-1">समर्थक सूची देखने के लिए लॉगिन करें</p>
            <div className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-sm font-semibold text-slate-700">ईमेल</Label>
                <Input
                  id="admin-email"
                  data-testid="admin-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-h-12"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-sm font-semibold text-slate-700">पासवर्ड</Label>
                <Input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="min-h-12"
                  required
                />
              </div>
              {loginError && (
                <p data-testid="admin-login-error" className="text-sm text-red-600">{loginError}</p>
              )}
              <Button
                type="submit"
                data-testid="admin-login-button"
                disabled={loginLoading}
                className="w-full min-h-12 text-base font-semibold bg-[#FF9933] hover:bg-[#E68A2E] text-white transition-colors duration-300"
              >
                {loginLoading ? "लॉगिन हो रहा है..." : "लॉगिन करें"}
              </Button>
            </div>
          </form>
        )}

        {auth && (
          <div data-testid="admin-dashboard">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 flex items-center gap-3">
                  <Users className="text-[#FF9933]" size={28} /> समर्थक सूची
                </h1>
                <p data-testid="supporters-total" className="text-sm text-slate-500 mt-1">
                  कुल समर्थक: {total}
                </p>
              </div>
              <Button
                data-testid="export-csv-button"
                onClick={handleExport}
                className="min-h-12 bg-[#138808] hover:bg-[#0f6b06] text-white font-semibold transition-colors duration-300"
              >
                <Download size={18} className="mr-2" /> CSV डाउनलोड करें
              </Button>
            </div>

            <div className="mt-8 bg-white rounded-lg shadow-md border border-slate-100 overflow-x-auto">
              <table data-testid="supporters-table" className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide text-xs">
                  <tr>
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">नाम</th>
                    <th className="px-4 py-3">मोबाइल</th>
                    <th className="px-4 py-3">जिला</th>
                    <th className="px-4 py-3">दर्ज तिथि</th>
                  </tr>
                </thead>
                <tbody>
                  {supporters.map((s, i) => (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                      <td className="px-4 py-3 font-semibold text-slate-800">{s.name}</td>
                      <td className="px-4 py-3 text-slate-700">{s.phone}</td>
                      <td className="px-4 py-3 text-slate-700">{s.district}</td>
                      <td className="px-4 py-3 text-slate-500">
                        {new Date(s.created_at).toLocaleString("hi-IN")}
                      </td>
                    </tr>
                  ))}
                  {supporters.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                        अभी कोई समर्थक दर्ज नहीं है
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
